/* What a `tt_settings` row is allowed to be.
 *
 * The console has this list too — `DEFAULTS` at the top of js/prefs.js — and
 * says of it that "a key that is not in it is not a setting, so hydrate drops
 * it". That is the right rule for a board: a station running an older build must
 * not act on a value it does not understand.
 *
 * It is the wrong rule for this app, and the difference is the whole reason this
 * file is not a copy of that one. An admin looking at a salon needs to see what
 * is actually in the table, including a key this build has never heard of —
 * because the reason to open the page is usually that something is set to
 * something odd. So the registry below classifies rather than filters: a known
 * key gets a label, a type and a validator; an unknown one is shown as raw JSON
 * on the Advanced page and marked as unrecognised.
 *
 * Pure functions over plain values. Nothing here imports Vue or Supabase, so the
 * validation can be tested without standing either up — the same reason
 * js/rules/queue.js was pulled out of the console's sync.js.
 */

/* The fourteen keys the console reads, in the order the pages present them.
 *
 *   page      which admin page owns it. 'advanced' means there is no curated
 *             control and the raw editor is the only way in — deliberate for
 *             the shapes that are edited as a whole or not at all.
 *   type      how the raw editor renders it, and which cleaner applies.
 *   fallback  what the console does when the row is absent. NOT a value to
 *             write: writing the default and leaving the default unset are the
 *             same board and different rows, and the second is the honest one.
 */
var KEYS = [
  {
    key: 'brand', page: 'branding', type: 'text', fallback: '',
    label: 'Business name',
    hint: 'What the top bar, the browser tab and every "the salon" in the console call this business. Empty falls back to "Console".',
    maxLength: 40
  },
  {
    key: 'logo', page: 'branding', type: 'json', fallback: null,
    label: 'Logo',
    hint: '{ dataUrl, width, height }. A data URI, not a link — the board has to come up branded with the salon\'s internet down.'
  },
  {
    key: 'theme', page: 'branding', type: 'json', fallback: null,
    label: 'Colour scheme',
    hint: '{ preset, custom }. `custom` overrides individual colours on top of the preset.'
  },
  {
    key: 'requestsOn', page: 'board', type: 'boolean', fallback: false,
    label: 'Track client requests',
    hint: 'Off out of the box. Turning it off hides requests already recorded; it never erases them.'
  },
  {
    key: 'showNextUp', page: 'board', type: 'boolean', fallback: true,
    label: 'Show who is up next',
    hint: 'Adds the "Next" column to every row of Next availability.'
  },
  {
    key: 'dueSoonMinutes', page: 'board', type: 'number', fallback: 10,
    label: 'Due now lead',
    hint: 'How many minutes before its start time a booking joins the Due now row.',
    min: 1, max: 120
  },
  {
    key: 'conflictBufferMinutes', page: 'board', type: 'number', fallback: 5,
    label: 'Conflict buffer',
    hint: 'How many minutes after a booking starts its technician can still be getting ready before Potential conflicts lists it. 0 is strict.',
    min: 0, max: 60
  },
  {
    key: 'startOffsetMinutes', page: 'board', type: 'number', fallback: 5,
    label: 'Early / late start margin',
    hint: 'How many minutes a booked turn can start before or after its booking before the progress bar marks it green (early) or red (late).',
    min: 1, max: 60
  },
  {
    key: 'fullTurnPrice', page: 'board', type: 'number', fallback: 40,
    label: 'Full turn price',
    hint: 'What a sitting has to cost, in dollars, to count as a full turn: the booked service with its options, plus any add-ons folded onto it. Anything less is a half turn. Turns with no booked price keep their type’s weight.',
    min: 1, max: 1000
  },
  {
    key: 'autoStop', page: 'board', type: 'boolean', fallback: true,
    label: 'Stop a turn on checkout',
    hint: 'Stops a running turn as of the checkout time when the booking system closes its ticket.'
  },
  {
    key: 'autoStart', page: 'board', type: 'boolean', fallback: true,
    label: 'Start the rest of a visit',
    hint: 'Seating a client starts every other service on that appointment that is due now.'
  },
  {
    key: 'bookedClientsGo', page: 'board', type: 'choice', fallback: 'book',
    choices: ['book', 'turn'],
    label: 'Who takes a booked client',
    hint: '"book" sends a client to the technician they booked; "turn" sends them to whoever is next in turn order.'
  },
  {
    key: 'hours', page: 'board', type: 'hours', fallback: null,
    label: 'Business hours',
    hint: '{ days: [Sun…Sat] }, each entry { open, close } as "HH:MM", or null for a day the salon is closed. Null all through means nobody has said, and the board asks nothing of the clock.'
  },
  {
    key: 'plugins', page: 'appointments', type: 'json', fallback: null,
    label: 'Plugins',
    hint: '{ scheduling: { enabled, pluginId } }. The channel slot writes itself in here too once one is switched on.'
  }
];

var BY_KEY = {};
KEYS.forEach(function (k) { BY_KEY[k.key] = k; });

function known(key) { return !!BY_KEY[key]; }
function meta(key) { return BY_KEY[key] || null; }
function forPage(page) {
  return KEYS.filter(function (k) { return k.page === page; });
}

/* ---------- cleaners ----------
 *
 * Each returns the value to store, or `undefined` to mean "that is not a value
 * this key can hold" — which the caller shows as a refusal rather than writing.
 * `null` is a real answer for several of these and is never a refusal: it is how
 * the console spells "nobody has set this", and it is what clearing a control
 * writes.
 */

function cleanBoolean(v) {
  if (typeof v === 'boolean') return v;
  if (v === 'true') return true;
  if (v === 'false') return false;
  return undefined;
}

// Bounded rather than refused. A number typed one digit too long is the common
// mistake and clamping it is what the console's own settings box does; refusing
// would leave the field showing a value the salon does not have.
function cleanNumber(v, m) {
  var n = Number(v);
  if (v === '' || v == null || !isFinite(n)) return undefined;
  n = Math.round(n);
  if (m && m.min != null) n = Math.max(m.min, n);
  if (m && m.max != null) n = Math.min(m.max, n);
  return n;
}

function cleanText(v, m) {
  var s = String(v == null ? '' : v).replace(/\s+/g, ' ').trim();
  return m && m.maxLength ? s.slice(0, m.maxLength) : s;
}

// One of the key's listed choices, or a refusal. An unlisted word is not
// guessed at: the console would read it as the default, and the admin should
// see that it was not taken.
function cleanChoice(v, m) {
  return m && m.choices && m.choices.indexOf(v) !== -1 ? v : undefined;
}

// "HH:MM", the shape `<input type="time">` produces and the store parses. A
// half-typed value is not written, which is why this refuses rather than
// guessing at "9" — a salon opening at 09:00 and one opening at 09:0 are not
// the same typo to a clock.
function cleanClock(v) {
  var s = String(v == null ? '' : v).trim();
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(s)) return undefined;
  return s;
}

/* Seven entries, each `{ open, close }` or null. The console reads
 * `raw.days[i]`, treats a missing entry as a closed day, and treats the whole
 * value being null as "nobody has said" — which behaves differently from seven
 * closed days, so an all-null week collapses back to null rather than being
 * stored as seven nulls.
 */
function cleanHours(v) {
  if (v == null) return null;
  if (typeof v !== 'object' || !Array.isArray(v.days)) return undefined;
  var days = [];
  var any = false;
  for (var i = 0; i < 7; i++) {
    var d = v.days[i];
    if (!d || d.closed) { days.push(null); continue; }
    var open = cleanClock(d.open);
    var close = cleanClock(d.close);
    if (open === undefined || close === undefined) { days.push(null); continue; }
    days.push({ open: open, close: close });
    any = true;
  }
  return any ? { days: days } : null;
}

// Anything the registry calls json, plus every unknown key. The value is stored
// as jsonb, so the only real constraint is that it is JSON at all — which the
// caller has already established by parsing it.
function cleanJson(v) { return v === undefined ? undefined : v; }

function clean(key, value) {
  var m = meta(key);
  if (!m) return cleanJson(value);
  if (value === null) return null;
  if (m.type === 'boolean') return cleanBoolean(value);
  if (m.type === 'number') return cleanNumber(value, m);
  if (m.type === 'text') return cleanText(value, m);
  if (m.type === 'hours') return cleanHours(value);
  if (m.type === 'choice') return cleanChoice(value, m);
  return cleanJson(value);
}

/* A one-line reading of a stored value, for the list on the Advanced page.
 *
 * The point is that a wrong value should be visible as wrong without opening
 * the editor. A logo reads as its dimensions rather than as four kilobytes of
 * base64; a week of hours reads as how many days are open.
 */
function describe(key, value) {
  var m = meta(key);
  if (value === null || value === undefined) return 'not set';

  if (m && m.type === 'boolean') return value ? 'on' : 'off';
  if (m && m.type === 'number') return String(value);
  if (m && m.type === 'text') return value === '' ? 'empty' : '"' + value + '"';

  if (key === 'logo' && value && value.dataUrl) {
    return value.width + '×' + value.height + ', ' +
      Math.round(String(value.dataUrl).length / 1024) + ' KB';
  }
  if (key === 'theme' && value) {
    var custom = Object.keys(value.custom || {}).length;
    return (value.preset || 'navy') + (custom ? ' + ' + custom + ' custom' : '');
  }
  if (key === 'hours' && value && Array.isArray(value.days)) {
    var open = value.days.filter(Boolean).length;
    return open === 0 ? 'closed all week' : open + ' day' + (open === 1 ? '' : 's') + ' open';
  }
  if (key === 'plugins' && value) {
    var s = value.scheduling || {};
    var c = value.channel || {};
    var bits = [];
    bits.push('scheduling ' + (s.enabled ? (s.pluginId || 'on') : 'off'));
    if (c.enabled != null) bits.push('channel ' + (c.enabled ? (c.pluginId || 'on') : 'off'));
    return bits.join(' · ');
  }

  var json = JSON.stringify(value);
  return json.length > 60 ? json.slice(0, 57) + '…' : json;
}

/* Rows the server returned, laid out as the Advanced page shows them: the known
 * keys in registry order first, then anything else, alphabetically, marked.
 *
 * A known key with no row is included with `present: false` — "this salon has
 * never set a theme" is a real and interesting answer, and a page that only
 * listed rows could not tell it from "this salon has no theme key because the
 * build is old".
 */
function inventory(rows) {
  var byKey = {};
  (rows || []).forEach(function (r) { byKey[r.key] = r; });

  var out = KEYS.map(function (m) {
    var row = byKey[m.key];
    return {
      key: m.key,
      label: m.label,
      hint: m.hint,
      type: m.type,
      page: m.page,
      known: true,
      present: !!row,
      value: row ? row.value : null,
      updatedAt: row ? row.updated_at : null
    };
  });

  Object.keys(byKey).sort().forEach(function (k) {
    if (known(k)) return;
    out.push({
      key: k,
      label: k,
      hint: 'This build does not know this key. The console ignores it — see DEFAULTS in js/prefs.js.',
      type: 'json',
      page: 'advanced',
      known: false,
      present: true,
      value: byKey[k].value,
      updatedAt: byKey[k].updated_at
    });
  });

  return out;
}

export const settingKeys = {
  KEYS: KEYS,
  known: known,
  meta: meta,
  forPage: forPage,
  clean: clean,
  cleanClock: cleanClock,
  cleanHours: cleanHours,
  describe: describe,
  inventory: inventory
};
